<template>
  <div
    class="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4 sm:px-6 lg:px-8"
  >
    <!-- Confirmation Modal -->
    <ConfirmationModal
      :isOpen="confirmModal.isOpen"
      :title="confirmModal.title"
      :message="confirmModal.message"
      :confirmText="confirmModal.confirmText"
      :isLoading="confirmModal.isLoading"
      @confirm="confirmModal.onConfirm"
      @cancel="closeModal"
    />
    <div class="mx-auto max-w-7xl">
      <!-- Header -->
      <div class="mb-8">
        <h1 class="text-4xl font-bold tracking-tight text-slate-900">
          Dummy Users Manager
        </h1>
        <p class="mt-2 text-lg text-slate-600">
          Create and manage test users for your school system
        </p>
      </div>

      <!-- Action Buttons -->
      <div class="flex flex-col sm:flex-row gap-3 mb-8">
        <button
          type="button"
          @click="deleteAll"
          class="flex-1 inline-flex items-center justify-center px-6 py-3 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 transition-colors duration-200"
        >
          Delete All Dummy Users
        </button>

        <button
          type="button"
          @click="createDummyTeachers"
          :disabled="loadingTeachers"
          class="flex-1 inline-flex items-center justify-center px-6 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
        >
          <svg
            v-if="!loadingTeachers"
            class="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 4v16m8-8H4"
            />
          </svg>
          <span
            v-else
            class="inline-block animate-spin mr-2"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </span>
          Add 5 Dummy Teachers
        </button>

        <button
          type="button"
          @click="createDummyStudents"
          :disabled="loadingStudents"
          class="flex-1 inline-flex items-center justify-center px-6 py-3 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
        >
          <svg
            v-if="!loadingStudents"
            class="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 4v16m8-8H4"
            />
          </svg>
          <span
            v-else
            class="inline-block animate-spin mr-2"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </span>
          Add 50 Dummy Students
        </button>

        <button
          @click="fetchDummyUsers"
          :disabled="loadingUsers"
          class="flex-1 inline-flex items-center justify-center px-6 py-3 rounded-lg bg-slate-600 text-white font-semibold hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
        >
          <svg
            v-if="!loadingUsers"
            class="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          <span
            v-else
            class="inline-block animate-spin mr-2"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </span>
          Refresh Users
        </button>
      </div>

      <!-- Filter -->
      <div class="mb-8 flex items-center gap-4">
        <label
          for="role-filter"
          class="font-semibold text-slate-700"
          >Filter by Role:</label
        >
        <select
          id="role-filter"
          v-model="selectedRole"
          @change="fetchDummyUsers"
          class="px-4 py-2 rounded-lg border border-slate-300 bg-white text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors"
        >
          <option value="">All Users</option>
          <option value="teacher">Teachers Only</option>
          <option value="student">Students Only</option>
        </select>
      </div>

      <!-- Messages -->
      <div
        v-if="errorMessage"
        class="mb-6 p-4 rounded-lg bg-red-50 border-l-4 border-red-500 text-red-800"
      >
        {{ errorMessage }}
      </div>
      <div
        v-if="successMessage"
        class="mb-6 p-4 rounded-lg bg-emerald-50 border-l-4 border-emerald-500 text-emerald-800"
      >
        {{ successMessage }}
      </div>

      <!-- Users Grid -->
      <div
        v-if="dummyUsers.length === 0"
        class="text-center py-12"
      >
        <svg
          class="mx-auto h-12 w-12 text-slate-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M17 20h5v-2a3 3 0 00-5.856-1.487M15 10a3 3 0 11-6 0 3 3 0 016 0zM6 20a3 3 0 003-3v-2a3 3 0 00-3-3H3a3 3 0 00-3 3v2a3 3 0 003 3h3z"
          />
        </svg>
        <p class="mt-4 text-lg text-slate-600">
          No dummy users found. Create some using the buttons above!
        </p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div
          v-for="user in dummyUsers"
          :key="user._id"
          :class="[
            'rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border',
            user.role === 'teacher'
              ? 'bg-blue-50 border-blue-200'
              : 'bg-green-50 border-green-200',
          ]"
        >
          <!-- Header -->
          <div
            :class="[
              'p-6 pb-4 border-b',
              user.role === 'teacher' ? 'border-blue-200' : 'border-green-200',
            ]"
          >
            <div class="flex items-start justify-between">
              <div class="flex items-center gap-4">
                <!-- Avatar -->
                <div
                  :class="[
                    'w-12 h-12 rounded-full text-white flex items-center justify-center text-lg font-bold',
                    user.role === 'teacher'
                      ? 'bg-gradient-to-br from-blue-500 to-blue-700'
                      : 'bg-gradient-to-br from-green-500 to-green-700',
                  ]"
                >
                  {{ user.name ? user.name.charAt(0).toUpperCase() : "U" }}
                </div>

                <div>
                  <h3 class="text-lg font-bold text-slate-900">
                    {{ user.name }}
                  </h3>
                  <p class="text-sm text-slate-500">
                    {{ user.phone || "No Phone" }}
                  </p>
                </div>
              </div>

              <!-- Role Badge -->
              <span
                :class="[
                  'px-3 py-1 rounded-full text-xs font-semibold',
                  user.role === 'teacher'
                    ? 'bg-blue-200 text-blue-800'
                    : 'bg-green-200 text-green-800',
                ]"
              >
                {{ user.role }}
              </span>
            </div>
          </div>

          <!-- Body -->
          <div class="p-6 space-y-3">
            <div class="flex justify-between text-sm">
              <span class="text-slate-600">Phone:</span>
              <span class="font-medium text-slate-900">
                {{ user.phone || "N/A" }}
              </span>
            </div>

            <!-- Teacher Fields -->
            <div
              v-if="user.role === 'teacher'"
              class="flex justify-between text-sm"
            >
              <span class="text-slate-600">Subject:</span>
              <span class="font-medium text-slate-900">
                {{ user.subject || "N/A" }}
              </span>
            </div>

            <!-- Student Fields -->
            <div
              v-if="user.role === 'student'"
              class="flex justify-between text-sm"
            >
              <span class="text-slate-600">Roll Number:</span>
              <span class="font-medium text-slate-900">
                {{ user.rollNumber || "N/A" }}
              </span>
            </div>

            <div
              v-if="user.role === 'student'"
              class="flex justify-between text-sm"
            >
              <span class="text-slate-600">Enrollment:</span>
              <span class="font-medium text-slate-900">
                {{ formatDate(user.enrollmentDate) }}
              </span>
            </div>
          </div>

          <!-- Footer -->
          <div
            :class="[
              'px-6 py-4 border-t flex gap-3',
              user.role === 'teacher'
                ? 'bg-blue-100 border-blue-200'
                : 'bg-green-100 border-green-200',
            ]"
          >
            <!-- Show ONLY for Teacher -->
            <button
              v-if="user.role === 'teacher'"
              @click="impersonate(user._id)"
              :disabled="loadingImpersonate === user._id"
              class="flex-1 px-4 py-2 rounded-lg bg-purple-600 text-white font-semibold hover:bg-purple-700 disabled:opacity-50"
            >
              {{
                loadingImpersonate === user._id ? "Loading..." : "Impersonate"
              }}
            </button>

            <!-- Delete (for both) -->
            <button
              @click="openDeleteModal(user._id, user.name)"
              class="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700"
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      <!-- Impersonation Status Bar -->
      <div
        v-if="impersonatedUser"
        class="fixed bottom-0 left-0 right-0 bg-slate-900 text-white py-4 px-6 shadow-2xl"
      >
        <div
          class="mx-auto max-w-7xl flex flex-col sm:flex-row items-center justify-between gap-4"
        >
          <p class="text-base font-semibold">
            Currently impersonating:
            <span class="text-blue-400">{{ impersonatedUser }}</span>
          </p>
          <button
            @click="switchBack"
            :disabled="loadingSwitchBack"
            class="px-6 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 inline-flex items-center gap-2"
          >
            <svg
              v-if="!loadingSwitchBack"
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M7 16V4m0 0L3 8m4-4l4 4"
              />
            </svg>
            <span
              v-else
              class="inline-block animate-spin"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </span>
            Switch Back
          </button>
        </div>
      </div>

      <!-- Padding for fixed bar -->
      <div
        v-if="impersonatedUser"
        class="h-24"
      ></div>
    </div>
  </div>
</template>

<script setup>
  import { ref, onMounted } from "vue";
  import jwtDecode from "jwt-decode";
  import ConfirmationModal from "@/components/ConfirmationModal.vue";

  import {
    addDummyTeachers,
    addDummyStudents,
    getDummyUsers,
    impersonateUser,
    switchBack as switchBackAPI,
    deleteAllDummyUsers,
    deleteUser,
  } from "@/services/salesService";

  const dummyUsers = ref([]);
  const selectedRole = ref("");
  const impersonatedUser = ref(false);

  const loadingTeachers = ref(false);
  const loadingStudents = ref(false);
  const loadingUsers = ref(false);
  const loadingImpersonate = ref(null);
  const loadingSwitchBack = ref(false);

  const errorMessage = ref("");
  const successMessage = ref("");

  // Confirmation Modal State
  const confirmModal = ref({
    isOpen: false,
    title: "Confirm Delete",
    message: "Are you sure you want to delete this user?",
    confirmText: "Delete",
    isLoading: false,
    userId: null,
    userName: "",
    onConfirm: null,
  });

  const clearMessages = () => {
    setTimeout(() => {
      errorMessage.value = "";
      successMessage.value = "";
    }, 5000);
  };

  const createDummyTeachers = async () => {
    loadingTeachers.value = true;
    errorMessage.value = "";
    successMessage.value = "";

    try {
      await addDummyTeachers();
      successMessage.value = "5 dummy teachers created successfully!";
      clearMessages();
      await fetchDummyUsers();
    } catch (error) {
      errorMessage.value =
        error.response?.data?.message ||
        "Failed to create teachers. Please try again.";
      clearMessages();
    } finally {
      loadingTeachers.value = false;
    }
  };

  const createDummyStudents = async () => {
    loadingStudents.value = true;
    errorMessage.value = "";
    successMessage.value = "";

    try {
      await addDummyStudents();
      successMessage.value = "5 dummy students created successfully!";
      clearMessages();
      await fetchDummyUsers();
    } catch (error) {
      errorMessage.value =
        error.response?.data?.message ||
        "Failed to create students. Please try again.";
      clearMessages();
    } finally {
      loadingStudents.value = false;
    }
  };

  const fetchDummyUsers = async () => {
    loadingUsers.value = true;
    errorMessage.value = "";

    try {
      const response = await getDummyUsers(selectedRole.value);
      dummyUsers.value = response.data.users || [];
    } catch (error) {
      errorMessage.value =
        error.response?.data?.error || "Failed to fetch users.";
      dummyUsers.value = [];
    } finally {
      loadingUsers.value = false;
    }
  };

  const impersonate = async (userId) => {
    loadingImpersonate.value = userId;
    errorMessage.value = "";

    try {
      const response = await impersonateUser(userId);

      // 🔥 STORE NEW TOKEN
      localStorage.setItem("token", response.data.token);

      // Reload app to refresh role
      window.location.reload();
    } catch (error) {
      errorMessage.value =
        error.response?.data?.error || "Failed to impersonate user.";
      clearMessages();
    } finally {
      loadingImpersonate.value = null;
    }
  };

  const switchBack = async () => {
    loadingSwitchBack.value = true;
    errorMessage.value = "";

    try {
      const response = await switchBackAPI();

      localStorage.setItem("token", response.data.token);

      impersonatedUser.value = false;

      window.location.reload();
    } catch (error) {
      errorMessage.value =
        error.response?.data?.error || "Failed to switch back.";
    } finally {
      loadingSwitchBack.value = false;
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  onMounted(() => {
    const token = localStorage.getItem("token");

    if (token) {
      try {
        const decoded = jwtDecode(token);

        if (decoded.impersonatedBy) {
          impersonatedUser.value = true;
        }
      } catch (err) {
        console.error("Invalid token");
      }
    }

    fetchDummyUsers();
  });
  const deleteAll = async () => {
    confirmModal.value = {
      isOpen: true,
      title: "Delete All Dummy Users",
      message:
        "Are you sure you want to delete all dummy users? This action cannot be undone.",
      confirmText: "Delete All",
      isLoading: false,
      userId: null,
      userName: "",
      onConfirm: confirmDeleteAll,
    };
  };

  const confirmDeleteAll = async () => {
    confirmModal.value.isLoading = true;
    errorMessage.value = "";
    successMessage.value = "";

    try {
      const response = await deleteAllDummyUsers();
      successMessage.value = response.data.message;
      closeModal();
      await fetchDummyUsers();
    } catch (error) {
      errorMessage.value =
        error.response?.data?.error || "Failed to delete dummy users.";
      closeModal();
    }
  };

  const openDeleteModal = (userId, userName) => {
    confirmModal.value = {
      isOpen: true,
      title: "Delete User",
      message: `Are you sure you want to delete ${userName}? This action cannot be undone.`,
      confirmText: "Delete",
      isLoading: false,
      userId: userId,
      userName: userName,
      onConfirm: confirmDeleteUser,
    };
  };

  const confirmDeleteUser = async () => {
    const userId = confirmModal.value.userId;
    confirmModal.value.isLoading = true;
    errorMessage.value = "";

    try {
      await deleteUser(userId);
      successMessage.value = `${confirmModal.value.userName} deleted successfully!`;
      closeModal();
      clearMessages();
      await fetchDummyUsers();
    } catch (error) {
      errorMessage.value =
        error.response?.data?.error || "Failed to delete user.";
      closeModal();
      clearMessages();
    }
  };

  const closeModal = () => {
    confirmModal.value.isOpen = false;
    confirmModal.value.isLoading = false;
    confirmModal.value.userId = null;
    confirmModal.value.userName = "";
  };
</script>
